import { useEffect, useMemo, useState } from "react";
import { Warning, ShieldWarning, LockSimple, ArrowCounterClockwise, Info } from "@phosphor-icons/react";
import {
  PERMISSIONS_BY_KEY,
  PER_THERAPIST_PERMISSION_KEYS,
  CLINIC_DEFAULT_PERMISSION_KEYS,
  deriveClinicRole,
  getRoleBaseline,
  applyRequires,
} from "../../config/permissionsRegistry";
import { getEffectivePermissions, getOverriddenPermissionKeys } from "../../utils/permissions";
import { SensitiveDataNotice } from "../shared/access";
import ConfirmDialog from "../ui/ConfirmDialog";
import { ROLE_OPTIONS, groupedPermissionEntries, presetDiff, memberName } from "./teamUtils";

const GROUPS = groupedPermissionEntries();

/**
 * "תפקיד והרשאות" tab. Fully controlled by the popup through `draft` /
 * `onDraftChange` so the Save button in the footer can stay shared.
 *
 * draft = { clinicRole, permissions: { key: bool }, overridden: Set<key> }
 *
 * `overridden` = keys the admin EXPLICITLY set away from the role baseline
 * (initially `therapist.permissionsOverridden`). Toggling a key to a value
 * that differs from the baseline adds it; toggling back removes it. The
 * baseline is the role preset, except the clinic-default keys (finance /
 * invoices / export) which follow the clinic-wide setting.
 */
function RolePermissionsTab({
  therapist,
  clinic,
  actorIsOwner,
  readOnly,
  readOnlyReason,
  draft,
  onDraftChange,
}) {
  const currentRole = deriveClinicRole(therapist);
  const [pendingRole, setPendingRole] = useState(null); // confirm role change
  const [pendingSensitiveOff, setPendingSensitiveOff] = useState(false);

  // Initialize draft from EFFECTIVE values (legacy docs ⇒ baseline ⇒ no change on save).
  useEffect(() => {
    if (draft) return;
    onDraftChange({
      clinicRole: currentRole === "owner" ? "owner" : currentRole,
      permissions: getEffectivePermissions(therapist, clinic),
      overridden: new Set(getOverriddenPermissionKeys(therapist, clinic)),
    });
    // Re-init only when a different member opens, or when the popup reset the
    // draft to null after a stale-save refusal (draft must survive clinic
    // snapshot churn while the admin is mid-edit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapist?.uid, therapist?.id, draft == null]);

  const role = draft?.clinicRole || currentRole;
  const perms = draft?.permissions || {};
  const overridden = draft?.overridden instanceof Set ? draft.overridden : new Set();
  const preset = useMemo(() => getRoleBaseline(role, clinic), [role, clinic]);
  const isAdminRole = role === "owner" || role === "manager";

  /** Override set after a change: keep only keys whose value differs from the baseline. */
  const nextOverridden = (nextPerms, touchedKey) => {
    const keys = new Set(overridden);
    if (touchedKey) keys.add(touchedKey);
    return new Set(
      Array.from(keys).filter(
        (k) => PERMISSIONS_BY_KEY[k] && !PERMISSIONS_BY_KEY[k].locked && nextPerms[k] !== preset[k],
      ),
    );
  };

  const setPerm = (key, value) => {
    const next = { ...perms, [key]: value };
    if (key === "clinical.viewSensitive" && value === false) {
      // forced off together with it
      next["treatments.write"] = false;
    }
    if (key === "clinical.viewSensitive" && value === true) {
      // re-enabling restores dependents to the role preset
      PER_THERAPIST_PERMISSION_KEYS.forEach((k) => {
        if (PERMISSIONS_BY_KEY[k].requires === "clinical.viewSensitive") next[k] = preset[k];
      });
    }
    const applied = applyRequires(next);
    onDraftChange({ clinicRole: role, permissions: applied, overridden: nextOverridden(applied, key) });
  };

  const requestToggle = (key, value) => {
    if (key === "clinical.viewSensitive" && value === false) {
      setPendingSensitiveOff(true);
      return;
    }
    setPerm(key, value);
  };

  const requestRole = (nextRole) => {
    if (nextRole === role) return;
    setPendingRole(nextRole);
  };

  // Role change ⇒ the new role's baseline applies in full, no personal overrides.
  const applyRole = (nextRole) => {
    onDraftChange({ clinicRole: nextRole, permissions: getRoleBaseline(nextRole, clinic), overridden: new Set() });
    setPendingRole(null);
  };

  const resetToPreset = () =>
    onDraftChange({ clinicRole: role, permissions: { ...preset }, overridden: new Set() });

  const diff = pendingRole ? presetDiff(perms, getRoleBaseline(pendingRole, clinic)) : null;

  if (currentRole === "owner") {
    return (
      <div className="tm-locked-toggle">
        <div className="tm-perm-text">
          <span className="tm-perm-title">
            <LockSimple size={16} weight="fill" /> בעל/ת הקליניקה
          </span>
          <span className="tm-perm-desc">
            לא ניתן לשנות את תפקיד הבעלים. גישה מלאה לכל חלקי המערכת.
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      {readOnly && (
        <div className="tm-note">
          <Info size={16} weight="fill" />
          <span>{readOnlyReason}</span>
        </div>
      )}

      {/* Role selector */}
      <div className="tm-field">
        <span className="tm-label">תפקיד</span>
        <div className="tm-seg" role="radiogroup" aria-label="תפקיד">
          {ROLE_OPTIONS.map((opt) => {
            const managerLocked = opt.key === "manager" && !actorIsOwner;
            return (
              <button
                key={opt.key}
                type="button"
                role="radio"
                aria-checked={role === opt.key}
                className={`tm-seg-opt ${role === opt.key ? "active" : ""}`}
                disabled={readOnly || managerLocked}
                title={managerLocked ? "רק בעל/ת הקליניקה יכול/ה למנות מנהלים" : undefined}
                onClick={() => requestRole(opt.key)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {!actorIsOwner && (
          <span className="tm-caption">רק בעל/ת הקליניקה יכול/ה למנות מנהלים</span>
        )}
        {role === "secretary" && (
          <span className="tm-caption">ללא יומן ומטופלים אישיים — עבודת קבלה ותיאום עבור מטפלים אחרים.</span>
        )}
      </div>

      {isAdminRole ? (
        <div className="tm-locked-toggle">
          <div className="tm-perm-text">
            <span className="tm-perm-title">גישת מנהל/ת מלאה</span>
            <span className="tm-perm-desc">
              כולל דשבורד ניהול, דוחות, פיננסים, ניהול צוות והגדרות. לצמצום ההרשאות יש לבחור תפקיד אחר.
            </span>
          </div>
          <label className="tm-switch">
            <input type="checkbox" checked readOnly disabled />
            <span className="tm-switch-track" />
          </label>
        </div>
      ) : (
        <>
          <div className="tm-meta" style={{ justifyContent: "space-between" }}>
            <span className="tm-caption">
              הרשאה אישית גוברת על ברירת המחדל של הקליניקה. שינויים נכנסים לתוקף מיידית.
            </span>
            {!readOnly && (
              <button type="button" className="tm-btn ghost" onClick={resetToPreset} style={{ minHeight: 32, padding: "0 10px" }}>
                <ArrowCounterClockwise size={14} weight="bold" />
                איפוס לברירות המחדל של התפקיד
              </button>
            )}
          </div>

          {GROUPS.map((group) => (
            <div key={group.id} className="tm-perm-group">
              <div className="tm-perm-group-title">{group.label}</div>
              {group.entries.map((entry) => {
                const value = perms[entry.key] === true;
                const locked = entry.locked === true;
                const requiresMet = !entry.requires || perms[entry.requires] === true;
                const disabled = readOnly || locked || !requiresMet;
                const isOverride = overridden.has(entry.key);
                const isClinicDefaultKey = CLINIC_DEFAULT_PERMISSION_KEYS.includes(entry.key);
                return (
                  <div key={entry.key} className={`tm-perm-row ${disabled ? "disabled" : ""}`}>
                    <div className="tm-perm-text">
                      <span className="tm-perm-title">
                        {entry.label}
                        {entry.sensitive && (
                          <span className={`tm-sens ${entry.sensitive === "medical" ? "medical" : ""}`} title={entry.sensitive === "medical" ? "חושף מידע רפואי" : "חושף פרטים אישיים של מטופלים אחרים"}>
                            {entry.sensitive === "medical" ? <ShieldWarning size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
                          </span>
                        )}
                        {isOverride && !locked && <span className="tm-chip warning">אישי</span>}
                      </span>
                      <span className="tm-perm-desc">{entry.description}</span>
                      <span className="tm-perm-meta">
                        {locked
                          ? "דורש תפקיד מנהל/ת קליניקה"
                          : !requiresMet
                            ? `דורש '${PERMISSIONS_BY_KEY[entry.requires]?.label}'`
                            : isClinicDefaultKey
                              ? `ברירת מחדל של הקליניקה: ${preset[entry.key] ? "כן" : "לא"}`
                              : `ברירת מחדל לתפקיד: ${preset[entry.key] ? "כן" : "לא"}`}
                      </span>
                      {entry.sensitive === "medical" && value && !locked && (
                        <SensitiveDataNotice compact />
                      )}
                    </div>
                    <label className="tm-switch">
                      <input
                        type="checkbox"
                        checked={value}
                        disabled={disabled}
                        onChange={(e) => requestToggle(entry.key, e.target.checked)}
                      />
                      <span className="tm-switch-track" />
                    </label>
                  </div>
                );
              })}
            </div>
          ))}
        </>
      )}

      {pendingRole && (
        <ConfirmDialog
          isOpen
          type="warning"
          title={`שינוי תפקיד ל${ROLE_OPTIONS.find((r) => r.key === pendingRole)?.label}`}
          message={`ההרשאות של ${memberName(therapist)} יוחלפו בברירות המחדל של התפקיד החדש.`}
          confirmText="שינוי תפקיד"
          onConfirm={() => applyRole(pendingRole)}
          onClose={() => setPendingRole(null)}
        >
          <div className="tm-kv" style={{ marginTop: 8 }}>
            <dt>יסגור:</dt>
            <dd>{diff?.closes.length ? diff.closes.join(" · ") : "—"}</dd>
            <dt>יפתח:</dt>
            <dd>{diff?.opens.length ? diff.opens.join(" · ") : "—"}</dd>
          </div>
        </ConfirmDialog>
      )}

      {pendingSensitiveOff && (
        <ConfirmDialog
          isOpen
          type="danger"
          title="האם לבטל גישה למידע רפואי?"
          message={`${memberName(therapist)} לא יוכל/תוכל לצפות או לכתוב דוחות טיפול והערות קליניות — גם של המטופלים שלו/ה. דוחות קיימים נשמרים ואינם נמחקים.`}
          confirmText="ביטול הגישה"
          onConfirm={() => {
            setPerm("clinical.viewSensitive", false);
            setPendingSensitiveOff(false);
          }}
          onClose={() => setPendingSensitiveOff(false)}
        />
      )}
    </>
  );
}

export default RolePermissionsTab;
